import React, { useState, useEffect } from "react";
import {
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  FormFeedback,
  FormText,
} from "reactstrap";
import { Trash2 } from "lucide-react";
import { showError } from "../components/AppToast";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;
const AMOUNT_REGEX = /^\d+(\.\d{0,2})?$/;
const MAX_FIXED_VALUE_LENGTH = 250;
const MAX_ADDRESS_FIXED_VALUE_LENGTH = 250;
const CASCADE_TYPES = [
  "select",
  "radio",
  "checkbox",
];
function validateFixedValue(value, fieldType) {
  if (!value) return "Value cannot be empty";
  const maxLen = fieldType === "address" ? MAX_ADDRESS_FIXED_VALUE_LENGTH : MAX_FIXED_VALUE_LENGTH;
  if (value.length > maxLen)
    return `Maximum ${maxLen} characters allowed.`;
  if (fieldType === "email" && !EMAIL_REGEX.test(value))
    return "Must be a valid email address (e.g. user@example.com).";
  if (fieldType === "phone") {
    if (!/^\d+$/.test(value)) return "Phone number must contain digits only.";
    if (!PHONE_REGEX.test(value)) return "Phone number must be exactly 10 digits.";
  }
  if (fieldType === "amount") {
    const num = Number(value);
    if (isNaN(num) || num < 0) return "Amount must be a number greater than 0.";
    if (!AMOUNT_REGEX.test(value)) return "Amount must be a valid number (up to 2 decimal places).";
  }
  return "";
}

const TYPES_WITHOUT_FIXED_VALUE = ["select", "dropdown", "radio", "checkbox"];

export default function FieldEditor({
  field,
  fields = [],
  onUpdate,
  onRemove,
  hideMandatoryDelete,
  mode,
  onValidationChange,
  receiptFieldLimit,
  receiptEnabledCount,
  showReceiptOrderErrors,
}) {
  const update = (patch) => onUpdate(field.id, { ...field, ...patch });

  const [optionsText, setOptionsText] = useState(
    Array.isArray(field.options) ? field.options.join(", ") : ""
  );
  const [fixedValueError, setFixedValueError] = useState("");

  useEffect(() => {
    setOptionsText(Array.isArray(field.options) ? field.options.join(", ") : "");
  }, [field.id]);

  const isExcel = mode === "excel";
  const isAddress = field.type === "address";
  const supportsFixedValue =
    !isExcel && !TYPES_WITHOUT_FIXED_VALUE.includes(field.type);

  const fixedValueMaxLen = isAddress ? MAX_ADDRESS_FIXED_VALUE_LENGTH : MAX_FIXED_VALUE_LENGTH;

  // Search-key fields are `mandatory` (locked/pinned), but they should still
  // be allowed to opt into receipt display — mandatory alone shouldn't hide
  // this toggle when the field is also the search key.
  const canToggleReceipt =
    !field.mandatory ||
    field.isSearchKey ||
    (field.mandatory && (field.type === "email" || field.type === "phone"));


  // Once the form-wide receipt cap is reached, block turning ON any
  // additional field's receipt toggle (fields already enabled can still be
  // turned off).
  const receiptAtLimit =
    receiptFieldLimit != null &&
    !field.receiptEnabled &&
    (receiptEnabledCount ?? 0) >= receiptFieldLimit;

  //   const receiptEnabledFields = fields.filter(
  //   (f) => f.receiptEnabled
  // );
  const receiptEnabledFields = fields.filter(
    (f) => f.receiptEnabled
  );

  const orderLimit =
    receiptEnabledFields.length +
    (!field.receiptEnabled ? 1 : 0);

  const usedReceiptOrders = fields
    .filter(
      (f) =>
        f.id !== field.id &&
        f.receiptEnabled &&
        f.receiptOrder != null
    )
    .map((f) => Number(f.receiptOrder));

  const availableOrders = [];

  for (let i = 1; i <= orderLimit; i++) {
    if (
      !usedReceiptOrders.includes(i) ||
      field.receiptOrder === i
    ) {
      availableOrders.push(i);
    }
  }

  // const usedReceiptOrders = fields
  //   .filter(
  //     (f) =>
  //       f.id !== field.id &&
  //       f.receiptOrder != null
  //   )
  //   .map((f) => Number(f.receiptOrder));

  // const availableOrders = [];

  // for (let i = 1; i <= fields.length; i++) {
  //   if (
  //     !usedReceiptOrders.includes(i) ||
  //     field.receiptOrder === i
  //   ) {
  //     availableOrders.push(i);
  //   }
  // }




  const handleFixedValueChange = (e) => {
    const val = e.target.value;
    const error = validateFixedValue(val, field.type);
    setFixedValueError(error);
    if (onValidationChange) onValidationChange(field.id, !!error);
    update({ fixedValue: val });
  };

  const handleFixedValueToggle = (checked) => {
    let error = "";
    if (checked) {
      error = validateFixedValue(field.fixedValue || "", field.type);
      setFixedValueError(error);
      if (onValidationChange) onValidationChange(field.id, !!error);
    } else {
      setFixedValueError("");
      if (onValidationChange) onValidationChange(field.id, false);
    }
    update({
      fixedValueEnabled: checked,
      fixedValue: checked ? field.fixedValue || "" : "",
      readOnly: checked ? true : false,
    });
  };

  const handleReceiptToggle = () => {
    if (receiptAtLimit) return;

    if (field.receiptEnabled) {
      update({
        receiptEnabled: false,
        receiptOrder: null,
      });
    } else {
      update({
        receiptEnabled: true,
        receiptOrder: null,
      });
    }
  };
  const getCascadeLevel = (fieldId, fields) => {
    let level = 1;

    let current = fields.find(
      (f) => f.id === fieldId
    );

    while (current?.parentFieldId) {
      level++;

      current = fields.find(
        (f) => f.id === current.parentFieldId
      );
    }

    return level;
  };

  const createsCircularReference = (
    childId,
    parentId,
    fields
  ) => {
    let current = parentId;

    while (current) {
      if (current === childId) {
        return true;
      }

      current = fields.find(
        (f) => f.id === current
      )?.parentFieldId;
    }

    return false;
  };
  const parentField = fields.find(
    (f) => f.id === field.parentFieldId
  );
 

  const fixedValueLength = (field.fixedValue ?? "").length;
  const fixedValueNearLimit = fixedValueLength >= fixedValueMaxLen - 20;

  const getFixedValueInput = () => {
    if (field.type === "amount")
      return (
        <Input
          value={field.fixedValue ?? ""}
          onChange={handleFixedValueChange}
          placeholder="e.g. 100.00"
          maxLength={fixedValueMaxLen}
          invalid={!!fixedValueError}
          type="number"
          min="0.01"
          step="0.01"
          bsSize="sm"
        />
      );
    if (field.type === "email")
      return (
        <Input
          value={field.fixedValue ?? ""}
          onChange={handleFixedValueChange}
          placeholder="e.g. user@example.com"
          maxLength={fixedValueMaxLen}
          invalid={!!fixedValueError}
          type="email"
          bsSize="sm"
        />
      );
    if (field.type === "phone")
      return (
        <Input
          value={field.fixedValue ?? ""}
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
            const error = validateFixedValue(onlyDigits, "phone");
            setFixedValueError(error);
            if (onValidationChange) onValidationChange(field.id, !!error);
            update({ fixedValue: onlyDigits });
          }}
          placeholder="e.g. 9876543210"
          maxLength={10}
          invalid={!!fixedValueError}
          type="text"
          inputMode="numeric"
          bsSize="sm"
        />
      );
    if (field.type === "date")
      return (
        <Input
          value={field.fixedValue ?? ""}
          onChange={handleFixedValueChange}
          invalid={!!fixedValueError}
          type="date"
          bsSize="sm"
        />
      );
    if (field.type === "address")
      return (
        <Input
          value={field.fixedValue ?? ""}
          onChange={handleFixedValueChange}
          placeholder="e.g. 221B Baker Street, London, NW1 6XE"
          maxLength={fixedValueMaxLen}
          invalid={!!fixedValueError}
          type="textarea"
          rows={3}
          bsSize="sm"
        />
      );
    return (
      <Input
        value={field.fixedValue ?? ""}
        onChange={handleFixedValueChange}
        placeholder="This value will be prefilled in the form."
        maxLength={fixedValueMaxLen}
        invalid={!!fixedValueError}
        type="text"
        bsSize="sm"
      />
    );
  };

  return (
    <div className="field-editor-panel">
      {/* Label */}
      <FormGroup className="mb-3">
        <Label className="fe-field-label">Label</Label>
        <Input
          value={field.label}
          onChange={(e) => update({ label: e.target.value })}
          bsSize="sm"
        />
      </FormGroup>

      {/* Placeholder — hidden when fixed value is on */}
      {!field.fixedValueEnabled && (
        <FormGroup className="mb-3">
          <Label className="fe-field-label">Placeholder</Label>
          <Input
            value={field.placeholder || ""}
            onChange={(e) => update({ placeholder: e.target.value })}
            bsSize="sm"
            placeholder={isAddress ? "e.g. Street, City, State, ZIP" : "Hint shown inside the field"}
            type={isAddress ? "textarea" : "text"}
            rows={isAddress ? 2 : undefined}
          />
        </FormGroup>
      )}

      {/* Help Text */}
      <FormGroup className="mb-3">
        <Label className="fe-field-label">Help Text</Label>
        <Input
          value={field.help || ""}
          onChange={(e) => update({ help: e.target.value })}
          bsSize="sm"
          placeholder="Shown below the field"
        />
      </FormGroup>

      {/* Options */}
      {(field.type === "select" || field.type === "radio" || field.type === "checkbox") && (
        <FormGroup className="mb-3">
          <Label className="fe-field-label">Options <span className="text-muted fw-normal">(comma separated)</span></Label>
          <Input
            value={optionsText}
            onChange={(e) => {
              const raw = e.target.value;
              setOptionsText(raw);
              const arr = raw.split(",").map((s) => s.trim()).filter(Boolean);
              update({ options: arr });
            }}
            placeholder="e.g. Option A, Option B"
            bsSize="sm"
          />
        </FormGroup>
      )}

      {/* Toggles */}
      <div className="fe-toggles-section">
        {/* Required */}
        {!field.mandatory && (
          <div className="fe-toggle-switch-row">
            <div className="fe-toggle-switch-info">
              <span className="fe-toggle-switch-label">Required</span>
              <span className="fe-toggle-switch-desc">User must fill this field</span>
            </div>
            <div
              className={`fe-switch ${field.required ? "fe-switch-on" : ""}`}
              onClick={() => update({ required: !field.required })}
              role="switch"
              aria-checked={!!field.required}
              tabIndex={0}
              onKeyDown={(e) => e.key === " " || e.key === "Enter" ? update({ required: !field.required }) : null}
            >
              <div className="fe-switch-thumb" />
            </div>
          </div>
        )}

        {/* Enable for receipt — shown for regular optional fields AND for
           the search-key field (which is `mandatory` but should still be
           able to opt into the receipt) */}
        {field.type === "email" && (
          <div className="fe-toggle-switch-row">
            <div className="fe-toggle-switch-info">
              <span className="fe-toggle-switch-label">
                Send Receipt To This Email
              </span>

              <span className="fe-toggle-switch-desc">
                Receipt emails will be sent to this field
              </span>
            </div>

            <div
              className={`fe-switch ${field.receiptEmailEnabled
                ? "fe-switch-on"
                : ""
                }`}
              onClick={() =>
                update({
                  receiptEmailEnabled:
                    !field.receiptEmailEnabled,
                })
              }
              role="switch"
              aria-checked={
                !!field.receiptEmailEnabled
              }
              tabIndex={0}
            >
              <div className="fe-switch-thumb" />
            </div>
          </div>
        )}
        {canToggleReceipt && (
          <div className="fe-toggle-switch-row">
            <div className="fe-toggle-switch-info">
              <span className="fe-toggle-switch-label">Enable for receipt</span>
              <span className="fe-toggle-switch-desc">
                {receiptAtLimit
                  ? `Receipt limit of ${receiptFieldLimit} fields reached`
                  : "This parameter will be displayed in receipt"}
              </span>
            </div>
            <div
              className={`fe-switch ${field.receiptEnabled ? "fe-switch-on" : ""}`}
              onClick={handleReceiptToggle}
              role="switch"
              aria-checked={!!field.receiptEnabled}
              aria-disabled={receiptAtLimit}
              tabIndex={0}
              title={receiptAtLimit ? `Receipt limit of ${receiptFieldLimit} reached` : undefined}
              style={receiptAtLimit ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              onKeyDown={(e) => (e.key === " " || e.key === "Enter") ? handleReceiptToggle() : null}
            >
              <div className="fe-switch-thumb" />
            </div>
          </div>
        )}
        {field.receiptEnabled && (
          <FormGroup className="mt-3">
            <Label className="fe-field-label">
              Receipt Display Order
            </Label>

            <Input
              type="select"
              bsSize="sm"
              value={field.receiptOrder || ""}
              onChange={(e) =>
                update({
                  receiptOrder:
                    e.target.value === ""
                      ? null
                      : Number(e.target.value),
                })
              }
            // onChange={(e) =>
            //   update({
            //     receiptOrder: Number(e.target.value),
            //   })
            // }
            >
              <option value="">
                Select Order
              </option>

              {availableOrders.map((order) => (
                <option key={order} value={order}>
                  {order}
                </option>
              ))}
            </Input>

            <FormText>
              Controls the order in which this field appears in the receipt.
            </FormText>
          </FormGroup>
        )}
        {CASCADE_TYPES.includes(field.type) && (
          <>
            <hr />

            <div className="mb-3">
              <Label className="fe-field-label">
                Cascade Configuration
              </Label>

              <div className="fe-toggle-switch-row">
                <div className="fe-toggle-switch-info">
                  <span className="fe-toggle-switch-label">
                    Enable Cascade
                  </span>


                  <span className="fe-toggle-switch-desc">
                    Show this field based on another field
                  </span>
                </div>

                <div
                  className={`fe-switch ${field.cascadeEnabled
                    ? "fe-switch-on"
                    : ""
                    }`}
                  onClick={() =>
                    update({
                      cascadeEnabled:
                        !field.cascadeEnabled,
                    })
                  }
                >
                  <div className="fe-switch-thumb" />
              </div>
              </div>
              {field.cascadeEnabled && (
                <FormGroup className="mt-3">
                  <Label className="fe-field-label">
                    Parent Field
                  </Label>

                  <Input
                    type="select"
                    bsSize="sm"
                    value={field.parentFieldId || ""}
                    onChange={(e) => {

                      const parentId = e.target.value;

                      if (!parentId) {
                        update({
                          parentFieldId: null,
                          cascadeLevel: 1,
                        });
                        return;
                      }

                      if (
                        createsCircularReference(
                          field.id,
                          parentId,
                          fields
                        )
                      ) {
                        showError(
                          "Circular dependency is not allowed."
                        );
                        return;
                      }

                      const parentLevel =
                        getCascadeLevel(
                          parentId,
                          fields
                        );

                      if (parentLevel >= 3) {
                        showError(
                          "Maximum 3 cascade levels are allowed."
                        );
                        return;
                      }

                      update({
                        parentFieldId: parentId,
                        cascadeLevel: parentLevel + 1,
                        cascadeMappings: {},
                      });
                    }}
                  >
                    <option value="">
                      Select Parent Field
                    </option>

                    {fields
                      .filter((f) => {
                        if (f.id === field.id) {
                          return false;
                        }

                        if (
                          !CASCADE_TYPES.includes(f.type)
                        ) {
                          return false;
                        }

                        const level =
                          getCascadeLevel(
                            f.id,
                            fields
                          );

                        return level < 3;
                      })
                      .map((f) => (
                        <option
                          key={f.id}
                          value={f.id}
                        >
                          {f.label}
                        </option>
                      ))}
                  </Input>

                  {field.parentFieldId && (
                    <FormText>
                      Cascade Level:{" "}
                      {field.cascadeLevel || 1}
                    </FormText>
                  )}

                  {field.parentFieldId &&
                    parentField?.options?.length > 0 &&
                    field.options?.length > 0 && (

                      <div className="mt-3">

                        <Label className="fe-field-label">
                          Value Mapping
                        </Label>

                        {parentField.options.map(
                          (parentOption) => (
                            <div
                              key={parentOption}
                              className="cascade-mapping-row"
                            >

                              <div className="cascade-parent">
                                {parentOption}
                              </div>

                              <div className="cascade-options">


                                {field.options.map(
                                  (childOption) => {

                                    const selected =
                                      field
                                        .cascadeMappings?.[
                                      parentOption
                                      ] || [];
                                   
                                    return (
                                      <div
                                        key={childOption}
                                        className="cascade-checkbox"
                                      >

                                        <Input
                                          type="checkbox"
                                          checked={selected.includes(
                                            childOption
                                          )}
                                          onChange={(e) => {

                                            const current =
                                              field.cascadeMappings || {};

                                            const existing =
                                              current[parentOption] || [];

                                            const updated =
                                              e.target.checked
                                                ? [
                                                  ...existing,
                                                  childOption,
                                                ]
                                                : existing.filter(
                                                  (x) => x !== childOption
                                                );

                                            update({
                                              cascadeMappings: {
                                                ...current,
                                                [parentOption]: updated,
                                              },
                                            });

                                           
                                          }}

                                        />

                                        {childOption}

                                      </div>
                                    );
                                  }
                                )}
                              </div>

                            </div>
                          )

                        )}

                      </div>
                    )}

                </FormGroup>
              )}
            {/* </div> */}
          </div>
      </>
        )}

      {/* Read Only */}
      {((isExcel && !field.isSearchKey) || (!isExcel && field.fixedValueEnabled)) && (
        <div className="fe-toggle-switch-row">
          <div className="fe-toggle-switch-info">
            <span className="fe-toggle-switch-label">Read Only</span>
            <span className="fe-toggle-switch-desc">Field cannot be edited</span>
          </div>
          <div
            className={`fe-switch ${field.readOnly ? "fe-switch-on" : ""}`}
            onClick={() => update({ readOnly: !field.readOnly })}
            role="switch"
            aria-checked={!!field.readOnly}
            tabIndex={0}
            onKeyDown={(e) => e.key === " " || e.key === "Enter" ? update({ readOnly: !field.readOnly }) : null}
          >
            <div className="fe-switch-thumb" />
          </div>
        </div>
      )}

      {/* Fixed Value */}
      {supportsFixedValue && (
        <div className="fe-toggle-switch-row">
          <div className="fe-toggle-switch-info">
            <span className="fe-toggle-switch-label">Fixed Value</span>
            <span className="fe-toggle-switch-desc">Prefill and lock this field</span>
          </div>
          <div
            className={`fe-switch ${field.fixedValueEnabled ? "fe-switch-on" : ""}`}
            onClick={() => handleFixedValueToggle(!field.fixedValueEnabled)}
            role="switch"
            aria-checked={!!field.fixedValueEnabled}
            tabIndex={0}
            onKeyDown={(e) => e.key === " " || e.key === "Enter" ? handleFixedValueToggle(!field.fixedValueEnabled) : null}
          >
            <div className="fe-switch-thumb" />
          </div>
        </div>
      )}

      {/* Split Amount */}
      {field.type === "amount" && !field.mandatory && (
        <div className="fe-toggle-switch-row">
          <div className="fe-toggle-switch-info">
            <span className="fe-toggle-switch-label">Split Amount</span>
            <span className="fe-toggle-switch-desc">Allow partial payments</span>
          </div>
          <div
            className={`fe-switch ${field.splitRequired ? "fe-switch-on" : ""}`}
            onClick={() => update({ splitRequired: !field.splitRequired })}
            role="switch"
            aria-checked={!!field.splitRequired}
            tabIndex={0}
            onKeyDown={(e) => e.key === " " || e.key === "Enter" ? update({ splitRequired: !field.splitRequired }) : null}
          >
            <div className="fe-switch-thumb" />
          </div>
        </div>
      )}
    </div>

      {/* Fixed Value Input */ }
  {
    supportsFixedValue && field.fixedValueEnabled && (
      <FormGroup className="mb-2 mt-3">
        <Label className="fe-field-label">
          Fixed Value{" "}
          {fixedValueNearLimit && (
            <span className="text-muted fw-normal">
              {fixedValueLength}/{fixedValueMaxLen}
            </span>
          )}
        </Label>
        {getFixedValueInput()}
        {fixedValueError ? (
          <FormFeedback className="d-block">{fixedValueError}</FormFeedback>
        ) : (
          <FormText>This value will be prefilled and locked.</FormText>
        )}
      </FormGroup>
    )
  }

  {/* Delete */ }
  {
    !hideMandatoryDelete && (
      <div className="fe-delete-row">
        <button
          className="fe-delete-btn"
          onClick={() => onRemove(field.id)}
          title="Remove this field"
        >
          <Trash2 size={13} />
          Remove Field
        </button>
      </div>
    )
  }
    </div >
  );
}